const RESERVED = new Set(["play", "menu"])

const LOBBY: Record<string, { categories?: string[]; producer?: string }> = {
  slots: { categories: ["Slots"] },
  cards: { categories: ["Blackjack", "Baccarat"] },
  roulette: { categories: ["Roulette"] },
  live: { categories: ["Live Dealer"] },
  originals: { producer: "Origami" }
}

export type CasinoLobbyPathInput = {
  categories?: string[]
  producer?: string
}

export type ParsedCasinoPath = {
  categorySlug?: string
  providerSlug?: string
  categories: string[]
  producer?: string
  hasFilter: boolean
}

export function toCasinoSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "")
}

export function isKnownLobbySlug(slug: string): boolean {
  return Object.hasOwn(LOBBY, slug)
}

export function lobbyAllowsProvider(slug: string): boolean {
  const filter = LOBBY[slug]
  return Boolean(filter && !filter.producer)
}

export function buildCasinoLobbyPath(input: CasinoLobbyPathInput): string {
  const categories = input.categories ?? []
  const slug = slugFor(categories, input.producer)

  if (input.producer && categories.length === 0) {
    return `/casino/${slug}`
  }

  if (input.producer) {
    return `/casino/${slug}/${toCasinoSlug(input.producer)}`
  }

  return `/casino/${slug}`
}

export function parseCasinoPathname(pathname: string): ParsedCasinoPath {
  const parts = pathname.split("/").filter(Boolean)
  if (parts[0] !== "casino" || !parts[1] || RESERVED.has(parts[1])) {
    return { categories: [], hasFilter: false }
  }

  const known = LOBBY[parts[1]]
  if (!known) {
    return { categories: [], hasFilter: false }
  }

  const providerSlug = parts[2]

  return {
    categorySlug: parts[1],
    providerSlug,
    categories: known.categories ?? [],
    producer: providerSlug ?? known.producer,
    hasFilter: true
  }
}

export function matchProducerName(
  candidates: Array<string | null | undefined>,
  slugOrName: string
): string {
  const exact = candidates.find((producer) => producer === slugOrName)
  if (exact) {
    return exact
  }

  const slug = toCasinoSlug(slugOrName)
  const bySlug = candidates.find(
    (producer) => producer && toCasinoSlug(producer) === slug
  )
  return bySlug ?? slugOrName
}

function slugFor(categories: string[], producer?: string): string {
  if (producer && categories.length === 0) {
    const branded = Object.entries(LOBBY).find(
      ([, filter]) =>
        filter.producer && toCasinoSlug(filter.producer) === toCasinoSlug(producer)
    )
    if (branded) {
      return branded[0]
    }
  }

  const known = Object.entries(LOBBY).find(([, filter]) => {
    const names = filter.categories ?? []
    return (
      !filter.producer &&
      names.length === categories.length &&
      names.every((name, index) => toCasinoSlug(name) === toCasinoSlug(categories[index] ?? ""))
    )
  })

  return known?.[0] ?? toCasinoSlug(categories[0] ?? "")
}
